import type { DiscoverChatbotCardViewModel } from "@next-platform/client-common-core";
import type { DropDownMenuItem } from "@next-platform/client-common-spicy-ui";
import React, { useCallback } from "react";

import DiscoverChatbotCardArt from "@/pages/Discover/components/DiscoverChatbotCardArt";
import DiscoverChatbotCardBody from "@/pages/Discover/components/DiscoverChatbotCardBody";
import { DISCOVER_CHATBOT_CARD_SURFACE_CLASS_NAME } from "@/pages/Discover/components/discoverChatbotCardDimensions";
import formatDiscoverChatbotCardCreatorHandle from "@/pages/Discover/components/formatDiscoverChatbotCardCreatorHandle";
import type { DiscoverChatbotCardLabels } from "@/pages/Discover/hooks/useDiscoverChatbotCardLabels";
import { cn } from "@/utils";

const ACTIVATION_KEYS = new Set(["Enter", " "]);
const DEFAULT_TEST_ID = "DiscoverChatbotCardView";
const CARD_CLASS_NAME = cn("group flex w-full cursor-pointer flex-col overflow-hidden", DISCOVER_CHATBOT_CARD_SURFACE_CLASS_NAME);

export type DiscoverChatbotCardViewProps = {
  model: DiscoverChatbotCardViewModel;
  labels: DiscoverChatbotCardLabels;
  dropdownMenuItems: DropDownMenuItem[];
  isAuthenticated: boolean;
  isFavoritePending?: boolean;
  onPress: (characterId: string) => void;
  onFavoritePress: (characterId: string) => void;
  "data-testid"?: string;
};

const buildAccessibilityLabel = (model: DiscoverChatbotCardViewModel): string => {
  const creatorHandle = formatDiscoverChatbotCardCreatorHandle(model.creatorUsername);
  return creatorHandle ? `${model.name}, ${creatorHandle}` : model.name;
};

/**
 * Web view for the Discovery chatbot card. Renders the shared
 * `DiscoverChatbotCardViewModel`; the whole card is the selection target while
 * the like/more controls stop propagation so they never also trigger card selection.
 */
const DiscoverChatbotCardView = ({
  model,
  labels,
  dropdownMenuItems,
  isAuthenticated,
  isFavoritePending = false,
  onPress,
  onFavoritePress,
  "data-testid": testId = DEFAULT_TEST_ID,
}: DiscoverChatbotCardViewProps) => {
  const handlePress = useCallback(() => onPress(model.characterId), [onPress, model.characterId]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Ignore keys that bubbled up from a nested control (favorite, more menu) - only the
      // card surface itself should open the overlay on Enter/Space.
      if (event.target !== event.currentTarget || !ACTIVATION_KEYS.has(event.key)) {
        return;
      }
      event.preventDefault();
      onPress(model.characterId);
    },
    [onPress, model.characterId],
  );

  return (
    <div
      data-testid={testId}
      role="button"
      tabIndex={0}
      aria-label={buildAccessibilityLabel(model)}
      className={CARD_CLASS_NAME}
      onClick={handlePress}
      onKeyDown={handleKeyDown}
    >
      <DiscoverChatbotCardArt
        model={model}
        labels={labels}
        dropdownMenuItems={dropdownMenuItems}
        isAuthenticated={isAuthenticated}
        isFavoritePending={isFavoritePending}
        testId={testId}
        onFavoritePress={onFavoritePress}
      />
      <DiscoverChatbotCardBody model={model} testId={testId} />
    </div>
  );
};

export default React.memo(DiscoverChatbotCardView);
